import { useEffect, useState } from 'react';
import { SequenceDetailsQuery, useSequenceDetailsLazyQuery } from '~/data';
import { select } from '~/lib/select';
import { Sequence, TrainingState, useTrainingStore } from '../state';
import { nextPathSequence } from '../state/navigation/requiredPath';
import { useTrainingParams } from '../useTrainingParams';

const selector = (s: TrainingState) =>
  select(s, 'activeItemPosition', 'items', 'highestUnlockedPosition');

function preloadImage(url: string) {
  const img = new Image();
  img.src = url;
}

function prefetchImages(
  items: NonNullable<SequenceDetailsQuery['sequence']['items']>
) {
  for (const item of items) {
    if ('items' in item && item.items) {
      // Recursively using prefetchImages with the item's items, even though they are of
      // a different type it's just not worth spending the time extracting that type right now
      // so I'm just using 'any'
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      prefetchImages(item.items as any);
    }
    if ('pictures' in item) {
      item.pictures?.forEach(p => p.mediaUrl && preloadImage(p.mediaUrl));
    }
  }
}

/**
 * Loads the next sequence and any images it references ahead of time so the user doesn't have to wait
 * Returns a boolean to indicate whether the next sequence is in a new chapter.
 */
export function usePrefetchSequence(sequenceId: string) {
  const { items, highestUnlockedPosition } = useTrainingStore(selector);
  const { learnerTrainingId } = useTrainingParams();
  const [sequenceQuery, { data }] = useSequenceDetailsLazyQuery();

  const [newChapter, setNewChapter] = useState(false);

  useEffect(() => {
    setNewChapter(false);
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (items?.length && sequenceId) {
      const current = items.find(
        (i): i is Sequence => i.kind === 'sequence' && i.id === sequenceId
      );

      // The slide Next leads to, past any chapter the learner tested out of
      const sequence =
        current && nextPathSequence(items, current, highestUnlockedPosition);

      if (current && sequence) {
        setNewChapter(sequence.chapterId !== current.chapterId);

        const variables = {
          learnerTrainingUuid: learnerTrainingId!,
          chapterSequenceId: sequence.chapterSequenceId
        };

        // delay the preloading of the next sequence in order to avoid affecting the transition
        timeoutId = setTimeout(() => {
          sequenceQuery({ variables });
        }, 3000);
      }
    }

    return () => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [items, sequenceId]);

  useEffect(() => {
    if (!data) return;
    prefetchImages(data.sequence.items || []);
  }, [data]);

  return newChapter;
}
